import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for camera management with device selection
 */
export const useCamera = () => {
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [cameraOn, setCameraOn] = useState(false);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);

  // Get list of available cameras
  const refreshCameras = useCallback(async () => {
    try {
      // Request permission first to get device labels
      await navigator.mediaDevices.getUserMedia({ video: true }).then(s => {
        s.getTracks().forEach(t => t.stop());
      });

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      
      setCameras(videoDevices);
      
      // Auto-select first camera if none selected
      if (!selectedCamera && videoDevices.length > 0) {
        setSelectedCamera(videoDevices[0].deviceId);
      }
      
      return videoDevices;
    } catch (err) {
      console.error('Error getting cameras:', err);
      setError('Camera access denied');
      return [];
    }
  }, [selectedCamera]);

  // Start camera with selected device
  const startCamera = useCallback(async (deviceId) => {
    try {
      // Stop existing stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      const constraints = {
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user',
          ...(deviceId && { deviceId: { exact: deviceId } })
        }
      };

      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(newStream);
      setError(null);

      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
        await videoRef.current.play();
      }

      return newStream;
    } catch (err) {
      console.error('Camera start error:', err);
      setError(err.message || 'Could not start camera');
      return null;
    }
  }, [stream]);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [stream]);

  // Toggle camera on/off
  const toggleCamera = useCallback(async () => {
    if (cameraOn) {
      stopCamera();
      setCameraOn(false);
    } else {
      await refreshCameras();
      const newStream = await startCamera(selectedCamera);
      if (newStream) {
        setCameraOn(true);
      }
    }
  }, [cameraOn, selectedCamera, refreshCameras, startCamera, stopCamera]);

  // Change camera device
  const changeCamera = useCallback(async (deviceId) => {
    setSelectedCamera(deviceId);
    if (cameraOn) {
      await startCamera(deviceId);
    }
  }, [cameraOn, startCamera]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  return {
    cameras,
    selectedCamera,
    cameraOn,
    stream,
    error,
    videoRef,
    refreshCameras,
    startCamera,
    stopCamera,
    toggleCamera,
    changeCamera
  };
};

export default useCamera;
